import { OUTCOMES_TOUR_BAND_PX, OUTCOMES_TOUR_CARD_RADIUS_PX, OUTCOMES_TOUR_EDGE_MARGIN_PX, OUTCOMES_TOUR_GAP_PX, OutcomesTourStep } from "consts";

// Pure geometry for the results-page tour, in viewport px.

export interface HighlightBox {
  top: number;
  left: number;
  width: number;
  height: number;
  radius: number;
}

interface CardPoint {
  top: number;
  left: number;
}

export interface TourLayout {
  box: HighlightBox | null;
  card: CardPoint;
}

export interface BoxSize {
  width: number;
  height: number;
}

export type CardPlacement = "below" | "above" | "corner";

const bandOutsetOf = (step: OutcomesTourStep) => (step.outset ? OUTCOMES_TOUR_BAND_PX : 0);

/** Inside the edge the band follows the area's own corners; outside it, the library's card radius. */
export const highlightBoxOf = (rect: Pick<DOMRect, "top" | "left" | "width" | "height">, step: OutcomesTourStep, areaRadius: number): HighlightBox => {
  const outset = bandOutsetOf(step);
  return {
    top: rect.top - outset,
    left: rect.left - outset,
    width: rect.width + outset * 2,
    height: rect.height + outset * 2,
    radius: outset || !areaRadius ? OUTCOMES_TOUR_CARD_RADIUS_PX : areaRadius,
  };
};

/** How much of the band's top sits under the sticky bar, and so is not drawn. */
export const clipTopOf = (box: HighlightBox, barBottom: number) => Math.max(0, barBottom - box.top);

export const isInBand = (box: HighlightBox, barBottom: number, x: number, y: number) =>
  x >= box.left && x <= box.left + box.width && y >= box.top + clipTopOf(box, barBottom) && y <= box.top + box.height;

/** Below the area if the card fits, else above, else the bottom-right corner. */
export const placementFor = (box: HighlightBox | null, cardHeight: number, visibleTop: number, viewportHeight: number): CardPlacement => {
  if (!box) return "corner";
  if (box.top + box.height + OUTCOMES_TOUR_GAP_PX + cardHeight <= viewportHeight - OUTCOMES_TOUR_EDGE_MARGIN_PX) return "below";
  if (box.top - OUTCOMES_TOUR_GAP_PX - cardHeight >= visibleTop + OUTCOMES_TOUR_EDGE_MARGIN_PX) return "above";
  return "corner";
};

/** Beside the area on its chosen side, but never off screen: scrolling the area away leaves the card at the edge. */
export const cardPositionFor = (box: HighlightBox | null, placement: CardPlacement, card: BoxSize, viewport: BoxSize, visibleTop: number): CardPoint => {
  const corner = { top: viewport.height - card.height - OUTCOMES_TOUR_EDGE_MARGIN_PX, left: viewport.width - card.width - OUTCOMES_TOUR_EDGE_MARGIN_PX };
  if (!box || placement === "corner") return corner;
  const left = Math.min(Math.max(box.left, OUTCOMES_TOUR_EDGE_MARGIN_PX), corner.left);
  const top = placement === "below" ? box.top + box.height + OUTCOMES_TOUR_GAP_PX : box.top - OUTCOMES_TOUR_GAP_PX - card.height;
  return { top: Math.min(Math.max(top, visibleTop + OUTCOMES_TOUR_EDGE_MARGIN_PX), corner.top), left };
};

export const easeInOutCubic = (progress: number) => (progress < 0.5 ? 4 * progress ** 3 : 1 - (-2 * progress + 2) ** 3 / 2);

const between = (from: number, to: number, progress: number) => from + (to - from) * progress;

/** A step between two layouts. With no band on either side, the band is left to the target layout. */
export const layoutBetween = (from: TourLayout, to: TourLayout, progress: number): TourLayout => {
  const card = { top: between(from.card.top, to.card.top, progress), left: between(from.card.left, to.card.left, progress) };
  if (!from.box || !to.box) return { box: to.box, card };
  return {
    card,
    box: {
      top: between(from.box.top, to.box.top, progress),
      left: between(from.box.left, to.box.left, progress),
      width: between(from.box.width, to.box.width, progress),
      height: between(from.box.height, to.box.height, progress),
      radius: between(from.box.radius, to.box.radius, progress),
    },
  };
};

/** Whole pixels: text on a fractional offset renders blurry. */
export const toWholePixels = (value: number) => `${Math.round(value)}px`;
